import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { UI_LANGUAGES, type UiLanguage } from '../shared/catalog';
import { api } from './api';

const STORAGE_KEY = 'radiostation.uiLanguage';

interface LanguageContextValue {
  language: UiLanguage;
  setLanguage: (language: UiLanguage) => void;
  text: (english: string, german: string) => string;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

function savedLanguage(): UiLanguage | undefined {
  const value = localStorage.getItem(STORAGE_KEY);
  return UI_LANGUAGES.includes(value as UiLanguage) ? (value as UiLanguage) : undefined;
}

export function LanguageProvider({ children }: PropsWithChildren) {
  const initiallySaved = useMemo(savedLanguage, []);
  const [language, setLanguageState] = useState<UiLanguage>(initiallySaved || 'de');

  useEffect(() => {
    if (initiallySaved) return;
    void api
      .publicConfig()
      .then((config) => setLanguageState(config.defaultLanguage))
      .catch(() => undefined);
  }, [initiallySaved]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      setLanguage: (nextLanguage) => {
        localStorage.setItem(STORAGE_KEY, nextLanguage);
        setLanguageState(nextLanguage);
      },
      text: (english, german) => (language === 'de' ? german : english),
    }),
    [language],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used inside LanguageProvider.');
  return context;
}
